import { useState, useRef, useEffect } from "react";
import { apiStream } from "@/lib/api";
import { Sparkles, Send, Loader2 } from "lucide-react";
import "./FredChat.css";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface QuickPrompt {
  label: string;
  text: string;
  emoji: string;
  color: string;
}

interface PromptCategory {
  title: string;
  prompts: QuickPrompt[];
}

const PROMPT_CATEGORIES: PromptCategory[] = [
  {
    title: "Objection Handling",
    prompts: [
      { label: "Too Expensive", text: "Customer says it's too expensive. How do I handle this?", emoji: "💰", color: "#ef4444" },
      { label: "Need to Think", text: "How to handle 'I need to think about it'", emoji: "🤔", color: "#f59e0b" },
      { label: "Online Prices", text: "Customer comparing our prices to online retailers", emoji: "🛒", color: "#3b82f6" },
      { label: "Spouse Approval", text: "Customer says they need to check with their spouse first", emoji: "👫", color: "#ec4899" },
      { label: "Not Ready", text: "Customer says they're just browsing and not ready to buy", emoji: "👀", color: "#8b5cf6" },
    ],
  },
  {
    title: "Closing Strategies",
    prompts: [
      { label: "Close Today", text: "How to close a deal today with an undecided customer", emoji: "🤝", color: "#10b981" },
      { label: "Financing Pitch", text: "Best way to pitch 0% financing to a hesitant buyer", emoji: "💳", color: "#06b6d4" },
      { label: "Urgency Close", text: "How to create urgency without being pushy", emoji: "⏰", color: "#f97316" },
      { label: "Bundle Deal", text: "How to bundle products together for a bigger sale", emoji: "📦", color: "#6366f1" },
      { label: "Delivery Incentive", text: "How to use free delivery as a closing incentive", emoji: "🚚", color: "#14b8a6" },
    ],
  },
  {
    title: "Upselling & Cross-Selling",
    prompts: [
      { label: "Sofa Upsell", text: "Best upsell strategies for someone buying a sofa", emoji: "🛋️", color: "#8b5cf6" },
      { label: "Protection Plan", text: "How to sell fabric protection and warranty plans", emoji: "🛡️", color: "#0ea5e9" },
      { label: "Room Packages", text: "Suggest complementary items to complete a living room", emoji: "🏠", color: "#22c55e" },
      { label: "Mattress Add-ons", text: "What accessories to recommend with a mattress purchase", emoji: "🛏️", color: "#a855f7" },
      { label: "Dining Upgrade", text: "How to upgrade a dining table customer to a full set", emoji: "🍽️", color: "#e11d48" },
    ],
  },
  {
    title: "Customer Engagement",
    prompts: [
      { label: "Ice Breakers", text: "Give me 5 great ice breaker questions for walk-in customers", emoji: "💬", color: "#f59e0b" },
      { label: "Follow-Up Text", text: "Write a follow-up text for a customer who visited yesterday", emoji: "📱", color: "#3b82f6" },
      { label: "Style Discovery", text: "Questions to discover a customer's home decor style", emoji: "🎨", color: "#ec4899" },
      { label: "Budget Talk", text: "How to bring up budget without making the customer uncomfortable", emoji: "💵", color: "#10b981" },
      { label: "Returning Customer", text: "Best approach for a customer who's coming back for the 2nd time", emoji: "🔄", color: "#6366f1" },
    ],
  },
];

export default function FredChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isStreaming) return;

    const userMsg: Message = { id: Date.now().toString(), role: "user", content: text.trim() };
    const assistantId = (Date.now() + 1).toString();
    const assistantMsg: Message = { id: assistantId, role: "assistant", content: "" };

    const updatedMessages = [...messages, userMsg];
    setMessages([...updatedMessages, assistantMsg]);
    setInput("");
    setIsStreaming(true);

    let content = "";
    try {
      await apiStream(
        "/chat",
        { messages: updatedMessages.map((m) => ({ role: m.role, content: m.content })) },
        (chunk) => {
          content += chunk;
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content } : m)));
        },
        () => {}
      );
    } catch (err: any) {
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: "Sorry, I couldn't process that. Please try again." } : m)));
    }
    setIsStreaming(false);
    inputRef.current?.focus();
  };

  return (
    <div className="chat-page">
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="fred-avatar"><Sparkles size={20} /></div>
          <div>
            <h2>Furniture Fred</h2>
            <span>AI Sales Co-Pilot</span>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-welcome">
            <div className="fred-avatar large"><Sparkles size={32} /></div>
            <h3>Hey! I'm Furniture Fred</h3>
            <p>Your AI sales co-pilot. Ask me about objection handling, closing strategies, product knowledge, or anything sales-related.</p>
            <div className="prompt-categories">
              {PROMPT_CATEGORIES.map((cat) => (
                <div key={cat.title} className="prompt-category">
                  <h4 className="prompt-category-title">{cat.title}</h4>
                  <div className="quick-prompts">
                    {cat.prompts.map((p) => (
                      <button key={p.label} className="quick-prompt-btn" onClick={() => sendMessage(p.text)}
                        style={{ "--prompt-color": p.color } as React.CSSProperties}>
                        <span className="prompt-emoji">{p.emoji}</span>
                        <span className="prompt-label">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div key={m.id} className={`message ${m.role}`}>
            {m.role === "assistant" && <div className="fred-avatar small"><Sparkles size={14} /></div>}
            <div className="message-bubble">
              {m.content || (isStreaming && m.role === "assistant" ? <Loader2 size={16} className="spinning" /> : null)}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-bar">
        <input ref={inputRef} type="text" placeholder="Ask Furniture Fred..." value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
          disabled={isStreaming} />
        <button className="btn btn-primary" onClick={() => sendMessage(input)} disabled={!input.trim() || isStreaming}>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
